import { useCallback, useEffect, useSyncExternalStore } from 'react';

import axiosInstance from '../services/axiosInstance';
import { eventService } from '../services/eventService';

/**
 * A countdown the server runs for an offered action (Start, Generate First
 * Page, Generate Architecture, Retry Failed Pages, and Looks Good on a first
 * page its review stopped). The server presses the button at `fire_at` whether
 * or not any tab is open; a browser only draws the time left and can ask it to
 * wait.
 */
export interface ServerAutoRun {
  entry_id: string;
  kind: string;
  button_key: string;
  label: string;
  status: string;
  fire_at: string | null;
}

interface AutoRunEvent extends ServerAutoRun {
  project_id?: string;
  server_now?: string;
}

interface Snapshot {
  runs: Record<string, ServerAutoRun>;
  /** Server clock minus this browser's clock, so every tab counts to the same instant. */
  offsetMs: number;
}

const EMPTY: Snapshot = { runs: {}, offsetMs: 0 };
const snapshots = new Map<string, Snapshot>();
const listeners = new Set<() => void>();

const publish = (projectId: string, next: Snapshot): void => {
  snapshots.set(projectId, next);
  listeners.forEach((listener) => listener());
};

const listen = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => { listeners.delete(listener); };
};

const offsetFrom = (serverNow: string | undefined, fallback: number): number => (
  serverNow ? Date.parse(serverNow) - Date.now() : fallback
);

const counting = (run: ServerAutoRun): boolean => run.status === 'scheduled' && Boolean(run.fire_at);

const withoutEntry = (runs: Record<string, ServerAutoRun>, entryId: string): Record<string, ServerAutoRun> => (
  Object.fromEntries(Object.entries(runs).filter(([key]) => key !== entryId))
);

/** Replace a project's countdowns with the server's list. */
export const loadAutoRuns = async (projectId: string): Promise<void> => {
  const { data } = await axiosInstance.get(`/api/v1/projects/${projectId}/auto-runs`);
  const runs = (data.auto_runs as ServerAutoRun[]).filter(counting);

  publish(projectId, {
    runs: Object.fromEntries(runs.map((run) => [run.entry_id, run])), offsetMs: offsetFrom(data.server_now, 0),
  });
};

/** One pushed change: a countdown armed, waited on, pressed or dropped. */
export const applyAutoRunEvent = (event: AutoRunEvent): void => {
  if (!event.project_id || !event.entry_id) return;
  const current = snapshots.get(event.project_id) ?? EMPTY;
  const rest = withoutEntry(current.runs, event.entry_id);

  publish(event.project_id, {
    runs: counting(event) ? { ...rest, [event.entry_id]: event } : rest,
    offsetMs: offsetFrom(event.server_now, current.offsetMs),
  });
};

/**
 * Wait: the offer stays, but the server will not press it on its own. With `buttonKey`, only that button's
 * countdown is stood down: one gate can show Polish More beside a counting Looks Good.
 */
export const waitAutoRun = async (projectId: string, entryId: string, buttonKey?: string): Promise<void> => {
  const run = snapshots.get(projectId)?.runs[entryId];

  if (!run || (buttonKey !== undefined && run.button_key !== buttonKey)) return;
  applyAutoRunEvent({ ...run, project_id: projectId, status: 'cancelled' });
  await axiosInstance.post(`/api/v1/projects/${projectId}/auto-runs/${entryId}/wait`);
};

/** Keep a project's countdowns current: on mount, on every (re)connect, and on each pushed change. */
export const useAutoRunFeed = (projectId: string | null): void => {
  useEffect(() => {
    if (!projectId) return undefined;
    const refresh = (): void => { loadAutoRuns(projectId).catch(() => undefined); };
    const stopRuns = eventService.subscribe('auto_run', (data) => applyAutoRunEvent(data as AutoRunEvent));
    const stopConnected = eventService.subscribe('connected', refresh);

    refresh();

    return () => { stopRuns(); stopConnected(); };
  }, [projectId]);
};

// One shared one-second clock, running only while some countdown is drawn.
let clock = Date.now();
let ticker: number | null = null;
const clockListeners = new Set<() => void>();

const tick = (): void => {
  clock = Date.now();
  clockListeners.forEach((listener) => listener());
};

const listenClock = (listener: () => void): (() => void) => {
  clockListeners.add(listener);
  if (ticker === null) {
    clock = Date.now();
    ticker = window.setInterval(tick, 1000);
  }

  return () => {
    clockListeners.delete(listener);
    if (!clockListeners.size && ticker !== null) { window.clearInterval(ticker); ticker = null; }
  };
};

const listenNowhere = (): (() => void) => () => undefined;

const useSecondsUntil = (target: number | null): number | null => {
  const now = useSyncExternalStore(target === null ? listenNowhere : listenClock, () => clock);

  return target === null ? null : Math.max(0, Math.ceil((target - now) / 1000));
};

/** Seconds until the server presses this entry's button, or null when it will not; `wait` stands it down. */
export const useServerAutoRun = (projectId: string | null, entryId: string | null, buttonKey: string) => {
  const snapshot = useSyncExternalStore(listen, () => (projectId ? snapshots.get(projectId) ?? EMPTY : EMPTY));
  const run = entryId ? snapshot.runs[entryId] : undefined;
  const target = run?.fire_at && run.button_key === buttonKey ? Date.parse(run.fire_at) - snapshot.offsetMs : null;
  const secondsLeft = useSecondsUntil(target);
  const wait = useCallback(() => {
    if (projectId && entryId) waitAutoRun(projectId, entryId, buttonKey).catch(() => undefined);
  }, [buttonKey, entryId, projectId]);

  return { secondsLeft, wait };
};
